import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";
import type { QuestionType } from "@/api/reportTemplates";
import { FormField, Input } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";

/**
 * Type-aware validation rules editor for the report-template form
 * builder. The structured sub-fields below the outer "Advanced settings"
 * collapsible serialize to a single opaque ValidationRulesJson string;
 * the canonical key set is:
 *
 *   Short answer / Long answer: { minLength?, maxLength?, pattern? }
 *   Number:                     { min?, max?, allowDecimals? }
 *   Date:                       { minDate?, maxDate? }     (ISO yyyy-mm-dd)
 *   Attach file:                { maxFileSizeMb?, allowedFileTypes? }
 *
 * Choose-one / multi-select / picker question types render no rules
 * because they don't take additional validation. A nested "Advanced
 * (JSON)" expander always remains for power users who need a rule the
 * structured editor doesn't cover.
 *
 * The whole section is collapsed by default — most admins don't need
 * validation rules; the Required toggle (rendered separately) covers
 * the common case.
 */

const FILE_TYPE_OPTIONS = ["PDF", "DOCX", "XLSX", "PNG", "JPG"] as const;

const PATTERN_TOOLTIP =
  "A regex pattern lets you require an answer to match a specific format. Example: ^\\d{10}$ means exactly 10 digits. Most admins can leave this blank.";

const TYPES_WITH_RULES: ReadonlySet<QuestionType> = new Set<QuestionType>([
  "Text",
  "TextArea",
  "Number",
  "Date",
  "FileAttachment",
]);

interface ValidationRulesEditorProps {
  questionType: QuestionType;
  value: string;
  onChange: (next: string) => void;
}

export function ValidationRulesEditor({
  questionType,
  value,
  onChange,
}: ValidationRulesEditorProps) {
  const [outerOpen, setOuterOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Parse the stored JSON once per change. If it's malformed we drop to
  // an empty record so the structured fields still render cleanly; the
  // raw value is still available in the Advanced expander unmodified.
  const parsed = useMemo(() => parseRules(value), [value]);

  // Whitelist the keys relevant to the current type so switching from
  // Text -> Number doesn't leave stale {minLength} on a Number rule.
  // Done as an effect so the warning shows once and the cleanup
  // persists into the saved JSON.
  const expectedKeys = relevantKeysFor(questionType);
  const hasOrphanedKeys = Object.keys(parsed).some(
    (k) => !expectedKeys.includes(k),
  );

  useEffect(() => {
    if (!hasOrphanedKeys || !TYPES_WITH_RULES.has(questionType)) return;
    const cleaned: Record<string, unknown> = {};
    for (const key of expectedKeys) {
      if (parsed[key] !== undefined) cleaned[key] = parsed[key];
    }
    onChange(serialize(cleaned));
    // We deliberately depend only on the question type so the cleanup
    // runs once when the type changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionType]);

  if (!TYPES_WITH_RULES.has(questionType)) {
    return null;
  }

  const update = (patch: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...parsed };
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === "" || v === null) {
        delete next[k];
      } else {
        next[k] = v;
      }
    }
    onChange(serialize(next));
  };

  const hasRules = Object.keys(parsed).length > 0;

  return (
    <div className="mt-space-2">
      <button
        type="button"
        className="flex items-center gap-space-1 text-caption font-display font-semibold text-gray-mid uppercase tracking-wider hover:text-onyx"
        onClick={() => setOuterOpen((o) => !o)}
        aria-expanded={outerOpen}
      >
        {outerOpen ? (
          <ChevronDown size={14} aria-hidden />
        ) : (
          <ChevronRight size={14} aria-hidden />
        )}
        Advanced settings
        {hasRules && (
          <span className="ml-space-1 text-gold-shadow normal-case tracking-normal">
            (configured)
          </span>
        )}
      </button>

      {outerOpen && (
        <div className="mt-space-2 space-y-space-2 border-l-2 border-border-light pl-space-3">
          <p className="text-caption text-gray-mid">
            Validation rules are optional. Most forms don't need them — the
            Required toggle covers most cases.
          </p>
          {hasOrphanedKeys && (
            <div className="text-caption text-status-warning bg-status-warning/10 border border-status-warning rounded-md p-space-2">
              Question type changed — non-applicable validation rules were
              cleared.
            </div>
          )}

          {questionType === "Text" || questionType === "TextArea" ? (
            <TextRules parsed={parsed} update={update} />
          ) : null}
          {questionType === "Number" ? (
            <NumberRules parsed={parsed} update={update} />
          ) : null}
          {questionType === "Date" ? (
            <DateRules parsed={parsed} update={update} />
          ) : null}
          {questionType === "FileAttachment" ? (
            <FileAttachmentRules parsed={parsed} update={update} />
          ) : null}

          <button
            type="button"
            className="flex items-center gap-space-1 text-caption text-gold-shadow hover:underline"
            onClick={() => setAdvancedOpen((o) => !o)}
            aria-expanded={advancedOpen}
          >
            {advancedOpen ? (
              <ChevronDown size={14} aria-hidden />
            ) : (
              <ChevronRight size={14} aria-hidden />
            )}
            Advanced (JSON)
          </button>
          {advancedOpen && (
            <FormField
              label="Validation rules JSON"
              helperText="Power-user field. Edit only when you need a rule the structured editor above doesn't expose."
            >
              <Textarea
                rows={3}
                placeholder='{"minLength": 5, "maxLength": 100}'
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="font-mono text-caption"
              />
            </FormField>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Inline help icon — renders a Lucide HelpCircle SVG inside an
 * accessible button. The previous inline rendering occasionally bled
 * through as a degree-symbol fallback when the surrounding label
 * normalized whitespace; wrapping in a discrete button with explicit
 * width/height makes the SVG render reliably and gives the tooltip a
 * focusable affordance for keyboard users.
 */
function HelpIcon({ label }: { label: string }) {
  return (
    <button
      type="button"
      tabIndex={0}
      title={label}
      aria-label={label}
      className="inline-flex h-4 w-4 items-center justify-center text-gray-mid hover:text-gold-shadow focus:outline-none focus:ring-1 focus:ring-gold rounded-full"
    >
      <HelpCircle size={14} strokeWidth={1.75} aria-hidden />
    </button>
  );
}

interface RuleProps {
  parsed: Record<string, unknown>;
  update: (patch: Record<string, unknown>) => void;
}

function TextRules({ parsed, update }: RuleProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-space-2">
      <FormField label="Min length">
        <Input
          type="number"
          min={0}
          value={numberValue(parsed.minLength)}
          onChange={(e) =>
            update({ minLength: parseOptionalInt(e.target.value) })
          }
        />
      </FormField>
      <FormField label="Max length">
        <Input
          type="number"
          min={0}
          value={numberValue(parsed.maxLength)}
          onChange={(e) =>
            update({ maxLength: parseOptionalInt(e.target.value) })
          }
        />
      </FormField>
      <FormField
        label={
          <span className="inline-flex items-center gap-space-1">
            Pattern (regex, advanced)
            <HelpIcon label={PATTERN_TOOLTIP} />
          </span>
        }
      >
        <Input
          placeholder="e.g. ^[A-Z]{2}\\d{6}$"
          value={stringValue(parsed.pattern)}
          onChange={(e) =>
            update({ pattern: e.target.value || undefined })
          }
        />
      </FormField>
    </div>
  );
}

function NumberRules({ parsed, update }: RuleProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-space-2">
      <FormField label="Min value">
        <Input
          type="number"
          value={numberValue(parsed.min)}
          onChange={(e) => update({ min: parseOptionalNumber(e.target.value) })}
        />
      </FormField>
      <FormField label="Max value">
        <Input
          type="number"
          value={numberValue(parsed.max)}
          onChange={(e) => update({ max: parseOptionalNumber(e.target.value) })}
        />
      </FormField>
      <FormField label="Allow decimals">
        <label className="flex items-center gap-space-2 text-small h-[2.25rem]">
          <input
            type="checkbox"
            checked={parsed.allowDecimals === true}
            onChange={(e) =>
              update({ allowDecimals: e.target.checked ? true : undefined })
            }
            className="h-4 w-4 accent-gold"
          />
          Decimals permitted
        </label>
      </FormField>
    </div>
  );
}

function DateRules({ parsed, update }: RuleProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
      <FormField label="Earliest date">
        <Input
          type="date"
          value={stringValue(parsed.minDate)}
          onChange={(e) =>
            update({ minDate: e.target.value || undefined })
          }
        />
      </FormField>
      <FormField label="Latest date">
        <Input
          type="date"
          value={stringValue(parsed.maxDate)}
          onChange={(e) =>
            update({ maxDate: e.target.value || undefined })
          }
        />
      </FormField>
    </div>
  );
}

function FileAttachmentRules({ parsed, update }: RuleProps) {
  const allowed: string[] = Array.isArray(parsed.allowedFileTypes)
    ? (parsed.allowedFileTypes as unknown[]).map(String)
    : [];

  const toggle = (type: string) => {
    const next = allowed.includes(type)
      ? allowed.filter((t) => t !== type)
      : [...allowed, type];
    update({ allowedFileTypes: next.length > 0 ? next : undefined });
  };

  return (
    <div className="space-y-space-2">
      <FormField label="Max file size (MB)">
        <Input
          type="number"
          min={0}
          value={numberValue(parsed.maxFileSizeMb)}
          onChange={(e) =>
            update({ maxFileSizeMb: parseOptionalInt(e.target.value) })
          }
        />
      </FormField>
      <FormField label="Allowed file types">
        <div className="flex flex-wrap gap-space-1">
          {FILE_TYPE_OPTIONS.map((type) => {
            const on = allowed.includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggle(type)}
                aria-pressed={on}
                className={`px-space-2 py-[4px] rounded-full text-caption font-display font-semibold border transition-colors ${
                  on
                    ? "bg-gold/15 border-gold text-gold-shadow"
                    : "bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow"
                }`}
              >
                {type}
              </button>
            );
          })}
        </div>
      </FormField>
    </div>
  );
}

// -- helpers --------------------------------------------------------------

function parseRules(value: string): Record<string, unknown> {
  if (!value || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function serialize(rules: Record<string, unknown>): string {
  return Object.keys(rules).length === 0 ? "" : JSON.stringify(rules);
}

function relevantKeysFor(type: QuestionType): string[] {
  switch (type) {
    case "Text":
    case "TextArea":
      return ["minLength", "maxLength", "pattern"];
    case "Number":
      return ["min", "max", "allowDecimals"];
    case "Date":
      return ["minDate", "maxDate"];
    case "FileAttachment":
      return ["maxFileSizeMb", "allowedFileTypes"];
    default:
      return [];
  }
}

function numberValue(v: unknown): number | "" {
  return typeof v === "number" && Number.isFinite(v) ? v : "";
}

function stringValue(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function parseOptionalInt(raw: string): number | undefined {
  if (raw === "") return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

function parseOptionalNumber(raw: string): number | undefined {
  if (raw === "") return undefined;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}
